import {
  EmptyState,
  FlexColumn,
  FlexRow,
  Loading,
  MaterialIcon,
  StyledSearchBar,
  useDebouncedSearch,
  useModal,
} from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { LabelSmall } from 'baseui/typography';
import { useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useLocation } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useCamera } from '../../hooks/useCamera';
import { useStyles } from '../../hooks/useStyles';
import { jobsIdsState, jobsPageState, jobsState, loadingJobsState, searchJobsState } from '../../storage/jobStates';
import { isValidPosition } from '../../utils/map';
import { FloatingCardContent } from '../layout/cards/FloatingCardContent';
import StyledTooltip from '../ui/StyledTooltip';
import JobCard from './JobCard';
import { JobModal } from './JobModal';
import JobsFilters from './JobsFilters';
import JobsSelectedFilters from './JobsSelectedFilters';

export function JobsList() {
  const { theme, classes, css } = useStyles();
  const jobIds = useRecoilValue(jobsIdsState);
  const loadingJobs = useRecoilValue(loadingJobsState);
  const { search, setSearch } = useDebouncedSearch(useRecoilState(searchJobsState));
  const paginatedJobs = useRecoilValue(jobsState);
  const [jobsPage, setJobsPage] = useRecoilState(jobsPageState);
  const scrollableRef = useRef<HTMLDivElement>(null);
  const { fitToLngLats } = useCamera();
  const [initialCenter, setInitialCenter] = useState(false);
  const { showModal, openModal, closeModal } = useModal();
  const queries = new URLSearchParams(useLocation().search);
  const jobId = queries.get('jobId');

  useEffect(() => {
    if (jobId && !showModal) openModal();
    if (!jobId && showModal) closeModal();
  }, [jobId]);

  useEffect(() => {
    if (!paginatedJobs || initialCenter) return;
    const jobsPosition = paginatedJobs?.items
      .filter((job) => isValidPosition(job.latitude, job.longitude))
      .map((job) => [job.longitude, job.latitude]) as number[][];
    fitToLngLats(jobsPosition);
    setInitialCenter(true);
  }, [paginatedJobs]);

  const onUpdate = () => {
    setJobsPage(0);
  };

  useEffect(() => {
    if (!scrollableRef.current) return;
    if (jobsPage === 0) {
      const scrollableDiv = scrollableRef.current;
      scrollableDiv.scrollTo(0, 0);
    }
  }, [jobsPage, scrollableRef]);

  return (
    <FloatingCardContent
      flexible
      gap={theme.sizing.scale500}
    >
      <FlexColumn gap={theme.sizing.scale300}>
        <FlexRow
          gap={theme.sizing.scale300}
          classNames={classes.centeredStart}
        >
          <div className={classes.flexible(1)}>
            <StyledSearchBar
              name="job-search"
              value={search}
              onChange={setSearch}
              clearable
              noMargin
            />
          </div>
          <JobsFilters />
          <StyledTooltip
            content={'Actualizar'}
            showArrow={false}
          >
            <Button
              size="compact"
              shape="square"
              onClick={onUpdate}
              kind="tertiary"
            >
              <MaterialIcon name={'sync'} />
            </Button>
          </StyledTooltip>
        </FlexRow>
        <JobsSelectedFilters />
      </FlexColumn>
      {!jobIds && !loadingJobs && <EmptyState title="No cuentas con ningún trabajo." />}
      {!jobIds && loadingJobs && <Loading />}
      {jobIds && (
        <>
          {!!paginatedJobs?.items.length && (
            <div
              id="scrollableDiv"
              className={css({ height: '100%', overflow: 'auto', marginBottom: '12px' })}
              ref={scrollableRef}
            >
              <InfiniteScroll
                dataLength={paginatedJobs?.items.length}
                next={() => setJobsPage(jobsPage + 1)}
                hasMore={paginatedJobs?.hasNext ?? false}
                loader={
                  <div className={css({ height: '80px', overflow: 'hidden' })}>
                    <Loading />
                  </div>
                }
                endMessage={
                  paginatedJobs?.hasNext && (
                    <p style={{ textAlign: 'center' }}>
                      <b>Cargando...</b>
                    </p>
                  )
                }
                scrollableTarget="scrollableDiv"
              >
                <FlexColumn gap={'8px'}>
                  {jobIds.map((jobId) => (
                    <JobCard
                      id={jobId}
                      key={jobId}
                    />
                  ))}
                </FlexColumn>
              </InfiniteScroll>
            </div>
          )}
          {!jobIds.length && (
            <EmptyState
              title="No se encontraron resultados"
              description="Intenta con otra busqueda"
            />
          )}
        </>
      )}
      <FlexRow>
        <LabelSmall>Total: {paginatedJobs?.total || 0}</LabelSmall>
      </FlexRow>
      {showModal && jobId && (
        <JobModal
          jobId={jobId}
          isOpen={showModal}
          onClose={closeModal}
        />
      )}
    </FloatingCardContent>
  );
}
